import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeftRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput, SelectField, Empty, Panel, inputCls } from "@/components/Bits";
import { MovDialog } from "@/components/MovDialog";
import { listTabs, tabCls } from "@/pages/ComissoesPage";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const Num = ({ l, v, cls = "" }) => <div className="text-center min-w-[64px]"><p className="text-[10px] font-bold uppercase text-[#5C6B66]">{l}</p><p className={`font-bold ${cls}`}>{v}</p></div>;

const ProdRow = ({ p, onMov, onMin }) => (
  <div className={`bg-white rounded-2xl border p-4 flex flex-wrap items-center gap-3 ${p.alerta ? "border-[#B05A40]" : "border-[#E2DDD3]"}`} data-testid={`estoque-item-${p.produto_id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-semibold truncate flex items-center gap-1">{p.alerta && <AlertTriangle className="h-4 w-4 text-[#B05A40]" data-testid={`estoque-alerta-${p.produto_id}`} />}{p.nome}</p>
      <p className="text-xs text-[#5C6B66] truncate">{p.tecnicos.map((t) => `${t.nome}: ${t.qtd}`).join(" · ") || "Nenhum material com técnicos"}</p>
    </div>
    <Num l="Depósito" v={p.deposito} /><Num l="Técnicos" v={p.com_tecnicos} /><Num l="Total" v={p.total} cls={p.alerta ? "text-[#B05A40]" : "text-[#1E4D2B]"} />
    <label className="text-center"><span className="block text-[10px] font-bold uppercase text-[#5C6B66]">Mínimo</span>
      <input type="number" min="0" step="any" defaultValue={p.estoque_minimo} onBlur={(e) => Number(e.target.value) !== p.estoque_minimo && onMin(p, e.target.value)} className={`${inputCls} h-10 w-20 px-2 text-center`} data-testid={`estoque-min-${p.produto_id}`} /></label>
    <button onClick={() => onMov(p)} className="h-11 px-3 rounded-xl bg-[#1E4D2B] text-white text-sm font-semibold flex items-center gap-1" data-testid={`estoque-mov-${p.produto_id}`}><ArrowLeftRight className="h-4 w-4" /> Movimentar</button>
  </div>
);

const Tecnico = ({ equipe }) => {
  const [tec, setTec] = useState("");
  const { data } = useFetch(tec ? "/estoque/tecnico" : null, { tecnico_id: tec });
  return (
    <div>
      <div className="sm:w-72 mb-4"><SelectField label="Técnico" id="et-tec" placeholder="Selecione" value={tec} onChange={(e) => setTec(e.target.value)} options={(equipe || []).map((u) => ({ value: u.user_id, label: u.name }))} data-testid="estoque-tec-select" /></div>
      {tec && data?.length === 0 && <Empty testId="estoque-tec-empty">Este técnico não tem materiais.</Empty>}
      {tec && !!data?.length && <Panel testId="estoque-tec-list"><ul className="divide-y divide-[#E2DDD3]">{data.map((s) => <li key={s.produto_id} className="py-3 flex justify-between"><span>{s.nome}</span><b>{s.qtd}</b></li>)}</ul></Panel>}
    </div>
  );
};

const Historico = () => {
  const { data } = useFetch("/estoque/movimentos");
  if (!data) return null;
  if (!data.length) return <Empty testId="estoque-mov-empty">Nenhuma movimentação ainda.</Empty>;
  return (
    <Panel testId="estoque-mov-list"><ul className="divide-y divide-[#E2DDD3]">{data.map((m) => (
      <li key={m.mov_id} className="py-3 text-sm flex justify-between gap-3"><span className="min-w-0"><b>{m.produto_nome}</b> · {m.tipo_label}{m.tecnico_nome ? ` · ${m.tecnico_nome}` : ""}{m.os_numero ? ` · ${m.os_numero}` : ""}
        <span className="block text-xs text-[#5C6B66]">{new Date(m.at).toLocaleString("pt-BR")} · {m.user_name}{m.observacao ? ` · ${m.observacao}` : ""}</span></span><b className="shrink-0">{m.quantidade}</b></li>))}</ul></Panel>
  );
};

export default function EstoquePage() {
  const [q, setQ] = useState("");
  const [mov, setMov] = useState(null);
  const [v, setV] = useState(0);
  const { data, reload } = useFetch("/estoque", { v });
  const { data: equipe } = useFetch("/ordens/equipe");
  const setMin = async (p, val) => { try { await api.put(`/estoque/${p.produto_id}/minimo`, { estoque_minimo: Number(val) || 0 }); toast.success("Mínimo salvo"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const list = (data || []).filter((p) => p.nome.toLowerCase().includes(q.toLowerCase()));
  const alertas = (data || []).filter((p) => p.alerta).length;
  return (
    <div data-testid="estoque-page">
      <PageHeader eyebrow="Gestão" title="Estoque" subtitle="Estoque geral (depósito + técnicos), estoque de cada técnico e baixa automática pelas OS." />
      {alertas > 0 && <div className="rounded-2xl bg-[#F5E6E0] border border-[#B05A40]/40 p-4 mb-4 font-semibold text-[#8C3F28] flex items-center gap-2" data-testid="estoque-alerta-banner"><AlertTriangle className="h-5 w-5" /> {alertas} produto(s) no estoque mínimo ou abaixo</div>}
      <Tabs defaultValue="geral">
        <TabsList className={listTabs}>
          <TabsTrigger value="geral" className={tabCls} data-testid="tab-estoque-geral">Estoque geral</TabsTrigger>
          <TabsTrigger value="tecnico" className={tabCls} data-testid="tab-estoque-tecnico">Por técnico</TabsTrigger>
          <TabsTrigger value="historico" className={tabCls} data-testid="tab-estoque-historico">Movimentações</TabsTrigger>
        </TabsList>
        <TabsContent value="geral">
          <div className="mb-4"><SearchInput value={q} onChange={setQ} placeholder="Buscar produto" testId="estoque-search" /></div>
          {data?.length === 0 && <Empty testId="estoque-empty">Cadastre produtos no menu Produtos para controlar o estoque.</Empty>}
          <div className="space-y-2">{list.map((p) => <ProdRow key={p.produto_id} p={p} onMov={setMov} onMin={setMin} />)}</div>
        </TabsContent>
        <TabsContent value="tecnico"><Tecnico key={v} equipe={equipe} /></TabsContent>
        <TabsContent value="historico"><Historico key={v} /></TabsContent>
      </Tabs>
      <MovDialog prod={mov} onOpenChange={(o) => !o && setMov(null)} equipe={equipe} onSaved={() => setV((x) => x + 1)} />
    </div>
  );
}
